import * as Yup from 'yup';

import { CartItem } from 'store/api/cart/types';
import { RefundItem } from 'store/api/refund/types';

import {
  calculateRefundData,
  calculateAvailableRefundAmounts,
} from './helpers';
import {
  FormValues,
  SummaryResult,
  ReturnPayment,
  FormValuesItems,
  GetInitialValues,
  GetValidationSchema,
  GetSummaryCardItems,
} from './types';

export const paymentTitles = ['', 'Amount', 'Tip', 'Processing Fee', ''];
export const paymentTemplate = '2fr repeat(3, 1fr) 64px';
export const paymentCellsMobile = [
  { row: '1/2', col: '1/3' },
  { row: '2/3', col: '1/2' },
  { row: '2/3', col: '2/3' },
  { row: '3/4', col: '1/2' },
  { row: '3/4', col: '2/3' },
];

export const template = '24px 2fr repeat(3, 1fr) 128px';
export const titles = ['Item', 'Quantity', 'Return Price', 'Amount', 'Damaged'];
export const cellsMobile = [
  { row: '1/2', col: '1/3' },
  { row: '2/3', col: '1/3' },
  { row: '3/4', col: '1/3' },
  { row: '4/5', col: '1/2' },
  { row: '4/5', col: '2/3' },
];

const sumQuantity = <T extends CartItem | RefundItem>(
  array: T[],
  filterFn: (item: T) => boolean
): number =>
  array.filter(filterFn).reduce((total, curr) => total + curr.quantity, 0);

const getExchangeQuantity = <T extends CartItem>(
  item: T,
  items: T[] = []
): number =>
  sumQuantity(items, (currItem) =>
    Boolean(
      currItem.isExchange &&
        currItem.productId === item.productId &&
        Math.abs(currItem.price) === Math.abs(item.price)
    )
  );

const getRefundedQuantity = ({
  data,
  cart,
}: GetInitialValues): FormValuesItems[] => {
  if (!data?.items || data.items.length === 0) {
    return [];
  }

  const cartItems = cart?.items || [];
  const refundItems =
    data?.refund?.flatMap((refund) => refund.refundItems) || [];
  const exchangeItems =
    data.exchanges?.flatMap((exchange) => exchange.items) || [];

  return data.items.map((item) => {
    const cartExchangeQuantity = getExchangeQuantity(item, cartItems);
    const exchangeQuantity = getExchangeQuantity(item, exchangeItems);
    const refundedQuantity = sumQuantity(
      refundItems,
      (refundItem) => refundItem.checkoutItemId === item.id
    );

    const availableQuantity =
      item.quantity -
      refundedQuantity -
      exchangeQuantity -
      cartExchangeQuantity;

    return {
      ...item,
      selected: false,
      isDamaged: false,
      price: item.price,
      availableQuantity,
      sellPrice: item.price,
      quantity: availableQuantity || 1,
    };
  });
};

export const getInitialValues = ({
  data,
  cart,
}: GetInitialValues): FormValues => ({
  storageId: '',
  returnPayments: [],
  approvalPassword: '',
  isReturnToInventory: false,
  items: getRefundedQuantity({ data, cart }) ?? [],
});

export const getValidationSchema = ({
  data,
  isExchange,
  isClosedEvent,
  hideManagerPin,
}: GetValidationSchema) => {
  const commonValidationSchema = {
    approvalPassword: hideManagerPin
      ? Yup.string()
      : Yup.string().length(4).required(),
    storageId: Yup.number()
      .positive()
      .when('isReturnToInventory', {
        otherwise: (schema) => schema.notRequired(),
        is: (value: boolean) => value && isClosedEvent,
        then: (schema) => schema.required('Storage is required field'),
      }),
    isReturnToInventory: Yup.boolean().test(
      'isReturned',
      "All items are damaged, you can't make a return to your inventory",
      function (isReturnToInventory) {
        const products = this.parent.items;
        const selectedProducts = products?.filter(
          (product: FormValuesItems) => product.selected
        );

        if (!selectedProducts.length) {
          return true;
        }

        const isAllDamaged = selectedProducts?.every(
          (product: FormValuesItems) => product.isDamaged
        );

        return !(isReturnToInventory && isAllDamaged);
      }
    ),
    items: Yup.array()
      .of(
        Yup.object().shape({
          selected: Yup.boolean(),
          sellPrice: Yup.number(),
          availableQuantity: Yup.number().required(),
          quantity: Yup.number()
            .positive('Quantity must be a positive number')
            .required('Quantity is a required field'),
          price: Yup.number()
            .max(
              Yup.ref('sellPrice'),
              "Can't be more than the amount of the sale"
            )
            .required('Price is a required field'),
        })
      )
      .min(1)
      .required()
      .test(
        'atLeastOneSelected',
        'At least one item must be selected',
        (items) => items.some((item) => item.selected)
      ),
  };

  if (isExchange) {
    return Yup.object().shape(commonValidationSchema);
  }

  return Yup.object().shape({
    ...commonValidationSchema,
    returnPayments: Yup.array()
      .of(
        Yup.object().shape({
          type: Yup.string().required(),
          amount: Yup.number().required(),
        })
      )
      .min(1, 'Payments field must have at least 1 items')
      .required()
      .test(
        'paymentsSum',
        'Total payment amount cannot exceed refundable amount',
        function (returnPayments) {
          const { items } = this.parent;

          const { totalRefundable } = calculateAvailableRefundAmounts({
            data,
            items,
            returnPayments: returnPayments as ReturnPayment[],
          });

          const totalPayments = Number(
            (returnPayments || [])
              .reduce((acc, payment) => acc + Number(payment.amount || 0), 0)
              .toFixed(2)
          );

          return totalPayments <= totalRefundable;
        }
      ),
  });
};

export const getSummaryCardItems = ({
  data,
  values,
  isExchange,
  maxAllowedTipRefund,
  maxAllowedProcessingFeeRefund,
}: GetSummaryCardItems) => {
  const { refundPrice } = calculateRefundData({
    data,
    items: values?.items || [],
  });

  const result: SummaryResult[] = [
    { label: 'Sub Total', price: data?.subTotal || 0 },
    {
      price: data?.taxAmount || 0,
      label: `Tax (${data?.tax}%)`,
    },
  ];

  if (data?.tip) {
    result.splice(2, 0, {
      label: 'Tip',
      price: data?.tip,
    });
  }

  if (data?.processingFeesAmount) {
    result.splice(2, 0, {
      label: 'Processing Fee',
      price: data?.processingFeesAmount,
    });
  }

  if (!isExchange) {
    result.push({
      price: refundPrice,
      withSeparate: true,
      label: 'Available Refund Amount',
    });

    if (maxAllowedTipRefund) {
      result.push({
        price: maxAllowedTipRefund,
        label: 'Available Tip Amount',
      });
    }

    if (maxAllowedProcessingFeeRefund) {
      result.push({
        label: 'Available Processing Fee',
        price: maxAllowedProcessingFeeRefund,
      });
    }
  }

  return result;
};
